import './index.less';

import { message } from 'antd';
import classNames from 'classnames';
import CryptoJS from 'crypto-js';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserProfile } from '@/hooks/useUserProfile';
import { uploadImg as uploadImgApi } from '@/services/user-feedback';

import FeedbackErrorDialog from '../FeedbackErrorDialog/FeedbackErrorDialog';

const IMG_MAX_NUMBER = 3;

const FeedbackUpload = ({
  urls,
  onLogin,
  onUpload,
}: {
  urls: string[];
  onLogin: () => void;
  onUpload: (params: string[]) => void;
}) => {
  const { t: $t } = useTranslation('user-feedback');
  const { userInfo, loading } = useUserProfile();
  const errorDialogRef = useRef<any>(null);
  const referenceUploadElRef = useRef<any>(null);

  const clickUpload = () => {
    if (!loading && !userInfo.id) {
      onLogin();
    }
  };

  const imgNumToast = () => {
    errorDialogRef.current?.open?.($t('feedbackInfoUploadTip'));
  };

  const referenceUpload = () => {
    if (referenceUploadElRef.current) {
      referenceUploadElRef.current.value = null;
    }
  };

  const getBaseUrl = (file: any, onloadend: (url: string) => void) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async (e: any) => {
      const base64Url = e.target?.result?.toString().split(';base64,')[1];
      onloadend(base64Url);
    };
  };

  const upload = async (
    name: string,
    type: string,
    url: string,
    idx: number,
  ) => {
    try {
      const { imageUrl } = (await uploadImgApi({
        img_data: {
          file_name: name,
          file_extension_type: type,
          file_md5_sum: CryptoJS.MD5(url).toString(),
          file_content: url,
        },
      })) as any;
      // const { ret_code, result } = {"ret_code":0,"ret_msg":"success","result":{"imageUrl":"https://gtd-falcon-prod.s3-ap-southeast-1.amazonaws.com/testnet/CSAT/a04c6e85327b8fedc87bac84b086876d.png"}}
      const newUrls = [...urls];
      if (idx > -1) {
        newUrls.splice(idx, 1, imageUrl);
      } else if (imageUrl) {
        newUrls.push(imageUrl);
      }
      onUpload(newUrls);
    } catch (error: any) {
      if (error.ret_code) {
        const i18nMessage = $t(error.ret_code, { ns: 'inverse-error-code' });
        message.error(
          `${error.ret_code}: ${
            i18nMessage === `${error.ret_code}` ? error.ret_msg : i18nMessage
          }`,
        );
      }
    }
  };

  const uploadImgItem = async (file: any, idx = -1) => {
    const { name, size } = file;
    const type = file.type.split('/')[1];
    // up to 1024k
    if (size >= 5 * 1024 * 1024) {
      const title = $t('newFeedbackInfoLimitTitle');
      const content = ($t('feedbackUploadSizeLimit') || '').replace(
        '{xx}',
        '5M',
      );
      errorDialogRef.current?.open?.(title, content);
      return;
    }
    getBaseUrl(file, (url) => {
      upload(name, type, url, idx);
    });
    referenceUpload();
  };

  const uploadImg = async (e: any) => {
    const { files } = e.target;
    const rest = IMG_MAX_NUMBER - urls.length;
    if (files.length > rest) {
      imgNumToast();
      return;
    }
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < files.length; i++) {
      uploadImgItem(files[i], -1);
    }
  };

  const modifyImage = async (index: number, e: any) => {
    const { files } = e.target;
    if (files.length > 1) {
      imgNumToast();
      return;
    }

    await uploadImgItem(files[0], index);
  };

  const deleteImage = (index: number) => {
    const newUrls = [...urls];
    newUrls.splice(index, 1);
    onUpload(newUrls);
  };

  return (
    <div className="upload-box">
      {urls.length < IMG_MAX_NUMBER ? (
        <div className="upload-btn-wrap">
          <div
            className={classNames('upload-btn', {
              'upload-btn-disabled': loading,
            })}
            onClick={clickUpload}
          >
            {userInfo.id ? (
              <div className="input-file">
                <input
                  ref={referenceUploadElRef}
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  onChange={uploadImg}
                  disabled={loading}
                />
              </div>
            ) : null}
            <em className="icon-upload-outline">
              <img
                src="/images/user-feedback/icons/upload-outline.svg"
                alt=""
              />
            </em>
            {$t('feedbackUploadText')}
          </div>
          <div className="upload-tips">
            <div>
              <span>{$t('feedbackUploadAttachments')}</span>
              <span>{$t('feedbackUploadFormatsAndLimit')}</span>
            </div>
          </div>
        </div>
      ) : null}
      {urls.length > 0 ? (
        <div className="upload-imgs-wrap">
          {urls.map((item, index) => (
            <div key={item} className="upload-img-item">
              <img src={item} className="upload-img" alt="" />
              <div className="upload-img-modify">
                <div className="input-file">
                  <input
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    multiple
                    onChange={(e) => modifyImage(index, e)}
                  />
                </div>
              </div>
              <span
                className="icon-upload-img-del"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteImage(index);
                }}
              ></span>
            </div>
          ))}
        </div>
      ) : null}
      <FeedbackErrorDialog
        ref={errorDialogRef}
        isUpload={true}
        isManySubmission={false}
      />
    </div>
  );
};

export default FeedbackUpload;
